import { IconHome } from '@tabler/icons-react';
import { NavLink, useLocation } from 'react-router-dom';
import { PoweredBy } from '../../components/PoweredBy';
import { hiddenSectionsNote, visibleSections } from '../access';
import { usePendingApprovalsCount } from '../queries';
import { sectionForPath } from '../sections';
import { useSession } from '../session';
import classes from './shell.module.css';

interface NavItemsProps {
  /** Drawer items are taller (48px) for touch. */
  size?: 'md' | 'lg';
  onNavigate?: () => void;
}

/** Home + the sections the role allows, with the HR approvals badge. Shared by the navbar and the drawer. */
export function NavItems({ size = 'md', onNavigate }: NavItemsProps) {
  const { access } = useSession();
  const { pathname } = useLocation();
  const pending = usePendingApprovalsCount();
  const current = sectionForPath(pathname)?.id;

  return (
    <ul className={classes.navList}>
      <li>
        <NavLink
          to="/"
          end
          className={classes.navItem}
          data-size={size}
          aria-current={pathname === '/' ? 'page' : undefined}
          onClick={onNavigate}
        >
          <IconHome size={size === 'lg' ? 22 : 21} stroke={2} className={classes.navIcon} />
          <span className={classes.navLabel}>Home</span>
        </NavLink>
      </li>
      {visibleSections(access).map((s) => {
        const active = current === s.id;
        // HR lands on its Overview (D91), which lists the approvals the badge counts.
        return (
          <li key={s.id}>
            <NavLink
              to={s.path}
              className={classes.navItem}
              data-size={size}
              aria-current={active ? 'page' : undefined}
              onClick={onNavigate}
            >
              <s.icon size={size === 'lg' ? 22 : 21} stroke={2} className={classes.navIcon} />
              <span className={classes.navLabel}>{s.label}</span>
              {s.id === 'hr' && pending > 0 && (
                <span className={classes.countBadge} aria-label={`${pending} approvals waiting`}>
                  {pending}
                </span>
              )}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

/** Footer: how many sections are hidden and why, plus the AutoMM credit. */
export function NavFooter() {
  const { access, user } = useSession();
  const note = hiddenSectionsNote(access, user.roleTitle);
  return (
    <div className={classes.navFooter}>
      {note && <div className={classes.hiddenNote}>{note}</div>}
      <div className={classes.navPowered}>
        <PoweredBy on="shell" />
      </div>
    </div>
  );
}
